/**
 * Rewrites a Supabase Storage public object URL to the on-the-fly image
 * transform endpoint, requesting a resized (and, for modern browsers, WebP)
 * variant. This dramatically reduces transferred bytes for product and
 * category images.
 *
 * Any URL that is not a Supabase `/storage/v1/object/public/` URL is returned
 * unchanged, so this is always safe to call.
 */
/**
 * Hosts whose images no longer exist (the old PrestaShop shop at
 * lianiki.mrpanda.gr is being decommissioned). Any image still pointing there
 * would return a 404, so we treat such URLs as "no image" and let the panda
 * placeholder show instead — avoiding broken images and noisy 404 requests.
 */
const DEAD_IMAGE_HOSTS = ["lianiki.mrpanda.gr"];

export function isDeadImageUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return DEAD_IMAGE_HOSTS.some((h) => url.includes(h));
}

/**
 * Category icons and manufacturer logos are served by the ops.mrpanda.gr worker
 * under their own roots (`/categories/...`, `/manufacturers/...`) — NOT under
 * `/image/` like product images. They support the same `?w=`/`?q=` resize +
 * automatic WebP/AVIF transform as product images.
 */
function isNonProductImagePath(path: string): boolean {
  return path.startsWith("categories/") || path.startsWith("manufacturers/");
}

/**
 * Resolves any catalog image URL to its (parameter-less) ops worker URL so it
 * actually loads and can be served as WebP/AVIF. Falls back to the original URL
 * for anything that is not a recognised catalog image.
 */
export function resolveCatalogAssetUrl(
  url: string | null | undefined,
): string | undefined {
  if (!url) return undefined;
  const target = toProxyBase(url);
  if (!target) return url;
  // For storage-render targets the original public object URL already loads
  // fine without any transform, so keep it parameter-less.
  return target.style === "supabase" ? target.original : target.base;
}

export function optimizeImage(
  url: string | null | undefined,
  opts: { width?: number; quality?: number } = {},
): string | undefined {
  return optimizeImageImpl(url, opts);
}

/**
 * Signed Supabase Storage URLs (`/storage/v1/object/sign/<bucket>/<path>?token=…`)
 * — e.g. τα social posts — δεν περνούν από τον ops worker. Τα γυρνάμε στο
 * render endpoint (`/storage/v1/render/image/sign/…`) ώστε να σερβίρονται
 * σμικρυμένα και σε WebP αντί για το πλήρες πρωτότυπο (~1.7 MB).
 */
const STORAGE_SIGN_MARKER = "/storage/v1/object/sign/";
const STORAGE_RENDER_SIGN_MARKER = "/storage/v1/render/image/sign/";

export function optimizeSignedStorageImage(
  url: string | null | undefined,
  opts: { width?: number; quality?: number } = {},
): string | undefined {
  if (!url) return undefined;
  const idx = url.indexOf(STORAGE_SIGN_MARKER);
  if (idx === -1) return url;
  const rest = url.slice(idx + STORAGE_SIGN_MARKER.length);
  const origin = url.slice(0, idx);
  const qIdx = rest.indexOf("?");
  const path = qIdx === -1 ? rest : rest.slice(0, qIdx);
  const query = qIdx === -1 ? "" : rest.slice(qIdx + 1);
  const params = new URLSearchParams(query);
  if (opts.width) params.set("width", String(Math.round(opts.width)));
  if (opts.quality) params.set("quality", String(Math.round(opts.quality)));
  return `${origin}${STORAGE_RENDER_SIGN_MARKER}${path}?${params.toString()}`;
}

function optimizeImageImpl(
  url: string | null | undefined,
  opts: { width?: number; quality?: number } = {},
): string | undefined {
  if (!url) return undefined;
  if (isDeadImageUrl(url)) return undefined;
  const target = toProxyBase(url);
  if (!target) return url; // external / non-catalog URL, leave untouched

  const { width, quality } = opts;
  const params = resizeParams(target.style, width, quality);
  if (params.length === 0) return target.base;
  return `${target.base}?${params.join("&")}`;
}

/**
 * Image widths the ops.mrpanda.gr image worker is allowed to resize to. Any
 * requested width is snapped to the smallest allowed value that is >= it (and
 * capped at the largest). Requesting a non-allowed width makes the worker fall
 * back to the original full-size image, so snapping is required.
 */
const ALLOWED_WIDTHS = [64, 96, 150, 200, 300, 400, 600, 800, 1000, 1200, 1600];

export function snapWidth(width: number): number {
  const w = Math.round(width);
  for (const allowed of ALLOWED_WIDTHS) {
    if (w <= allowed) return allowed;
  }
  return ALLOWED_WIDTHS[ALLOWED_WIDTHS.length - 1];
}

/**
 * Returns the parameter-less proxy base URL for a catalog image, or
 * `undefined` if the URL is not a catalog image (and should be left untouched).
 *
 * - `https://ops.mrpanda.gr/image/<file>` → stripped of any query string (ops).
 * - Supabase `/storage/v1/object/public/product-images/<path>` URLs:
 *   - legacy `prestashop/<id>.jpg` → ops worker (folder dropped).
 *   - everything else (e.g. admin-uploaded `new-products/...`) is NOT served by
 *     the ops worker, so it is routed to Supabase Storage's image-render
 *     endpoint instead so it actually loads (and can still be resized).
 */
const OPS_IMAGE_PREFIX = "https://ops.mrpanda.gr/image/";
const OPS_HOST = "https://ops.mrpanda.gr/";
const STORAGE_OBJECT_MARKER = "/storage/v1/object/public/product-images/";
const STORAGE_RENDER_MARKER = "/storage/v1/render/image/public/product-images/";

/**
 * Ο ops worker απαγορεύει τα πάντα στο robots.txt (`Disallow: /`), οπότε το
 * Googlebot-Image δεν μπορεί να δει καμία φωτογραφία προϊόντος. Σερβίρουμε τις
 * ίδιες εικόνες μέσα από το δικό μας domain (proxy route) που τις επιτρέπει.
 */
const IMG_PROXY_PREFIX = "/api/public/img/";

/** ops URL → proxy URL στο δικό μας domain (χωρίς query string). */
function toProxyUrl(opsUrl: string): string {
  return IMG_PROXY_PREFIX + opsUrl.slice(OPS_HOST.length);
}


/**
 * Admin-uploaded images (e.g. `new-products/...`) currently come straight from
 * Supabase Storage's render endpoint, which stamps a short 1h `Cache-Control`
 * on every file and skips the OPS worker's WebP/AVIF + long-cache pipeline.
 *
 * Flip this to `true` ONLY after the OPS worker is serving the storage uploads
 * under `https://ops.mrpanda.gr/image/<full-path>` (e.g.
 * `https://ops.mrpanda.gr/image/new-products/178….jpeg`) with
 * `Cache-Control: public, max-age=31536000, immutable`. Once on, these images
 * route through the worker — gaining a 1-year cache and better compression.
 *
 * Enabled: the OPS worker now serves any `product-images` bucket path
 * (including `new-products/...`) under `https://ops.mrpanda.gr/image/<full-path>`
 * with `Cache-Control: public, max-age=31536000, immutable` and `?w=`/`?q=`
 * resize + auto WebP/AVIF. Verified live.
 */
const OPS_SERVES_STORAGE_UPLOADS = true;

type ProxyTarget = { base: string; style: "ops" | "supabase"; original: string };

/**
 * Builds the resize query parameters for a given proxy style. The ops worker
 * uses `w`/`q`; the Supabase render endpoint uses `width`/`quality`.
 */
function resizeParams(
  style: "ops" | "supabase",
  width?: number,
  quality?: number,
): string[] {
  const params: string[] = [];
  const q = quality && quality !== 82 ? Math.max(40, Math.min(95, Math.round(quality))) : undefined;
  if (style === "ops") {
    // Το proxy μας δέχεται `width`/`quality` (το `q=` το μπλοκάρει το robots.txt).
    if (width) params.push(`width=${snapWidth(width)}`);
    // The worker defaults to q=82, so only send when a non-default value is asked for.
    if (q !== undefined) params.push(`quality=${q}`);

  } else {
    if (width) {
      // Square bounding box + `resize=contain` so the whole product stays
      // visible (preserves aspect ratio). Without it the render endpoint
      // defaults to `cover`, which crops the image.
      const w = snapWidth(width);
      params.push(`width=${w}`, `height=${w}`, "resize=contain");
    }
    if (q !== undefined) params.push(`quality=${q}`);
  }
  return params;
}

function toProxyBase(url: string): ProxyTarget | undefined {
  // Extract the storage-relative path from any recognised catalog URL form.
  let path: string | undefined;
  let storageOrigin: string | undefined;
  if (url.startsWith(IMG_PROXY_PREFIX)) {
    // Ήδη proxy URL (π.χ. από resolveCatalogAssetUrl): κρατάμε το path ώστε να
    // μπορούν να προστεθούν οι παράμετροι resize αντί να επιστραφεί αυτούσιο.
    path = url.slice(IMG_PROXY_PREFIX.length);
    if (path.startsWith("image/")) path = path.slice("image/".length);
  } else if (url.startsWith(OPS_IMAGE_PREFIX)) {
    path = url.slice(OPS_IMAGE_PREFIX.length);
  } else if (url.startsWith(OPS_HOST)) {
    path = url.slice(OPS_HOST.length);
  } else {
    const idx = url.indexOf(STORAGE_OBJECT_MARKER);
    if (idx !== -1) {
      storageOrigin = url.slice(0, idx);
      path = url.slice(idx + STORAGE_OBJECT_MARKER.length);
    }
  }
  if (path === undefined) return undefined;
  const q = path.indexOf("?");
  if (q !== -1) path = path.slice(0, q);

  // Category icons & manufacturer logos live under their own worker roots
  // (NOT under /image/), e.g. https://ops.mrpanda.gr/categories/300/icon.png
  if (isNonProductImagePath(path)) {
    return { base: toProxyUrl(OPS_HOST + path), style: "ops", original: url };
  }


  // Images that come straight from Supabase Storage (admin uploads such as
  // `new-products/...`) are NOT served by the ops worker — only legacy
  // PrestaShop imports are. Serve those via Supabase's image-render endpoint.
  if (storageOrigin !== undefined && !path.startsWith("prestashop/")) {
    // Once OPS serves these uploads, route them through the worker (long cache
    // + WebP/AVIF) at /image/<full-path>, keeping the `new-products/` folder.
    if (OPS_SERVES_STORAGE_UPLOADS) {
      return { base: toProxyUrl(OPS_IMAGE_PREFIX + path), style: "ops", original: url };

    }
    return {
      base: `${storageOrigin}${STORAGE_RENDER_MARKER}${path}`,
      style: "supabase",
      original: url,
    };
  }

  // Product images: drop the bare PrestaShop folder, but keep `prestashop-import/`.
  if (path.startsWith("prestashop/")) {
    path = path.slice("prestashop/".length);
  }
  return { base: toProxyUrl(OPS_IMAGE_PREFIX + path), style: "ops", original: url };
}

/**
 * Builds a responsive `srcset` string of proxy URLs at the given widths.
 * Returns `undefined` for non-catalog URLs (where resizing is not available).
 */
export function imageSrcSet(
  url: string | null | undefined,
  widths: number[],
  quality?: number,
): string | undefined {
  if (!url || isDeadImageUrl(url)) return undefined;
  const target = toProxyBase(url);
  if (!target) return undefined;
  const seen = new Set<number>();
  const entries: string[] = [];
  for (const w of widths) {
    const sw = snapWidth(w);
    if (seen.has(sw)) continue;
    seen.add(sw);
    const params = resizeParams(target.style, sw, quality);
    entries.push(`${target.base}?${params.join("&")} ${sw}w`);
  }
  return entries.length ? entries.join(", ") : undefined;
}
